import React, { useState } from 'react';
import { Modal } from '../../components/common/Modal';
import { CuppingFormFooter, CuppingFormSections, CuppingModeSelector, CuppingMetaFields } from './components/CuppingFormSections';
import { useCuppingProfileForm, type CuppingProfileFormProps } from '../../hooks/inventory-hooks/useCuppingProfileForm';
import { useLanguage } from '../../features/language/useLanguage';

export const CuppingProfileForm: React.FC<CuppingProfileFormProps> = (props) => {
  const { t } = useLanguage();
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    mode,
    setMode,
    maxScore,
    scaComputedFinalScore,
    handleSave
  } = useCuppingProfileForm(props);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingData, setPendingData] = useState<Parameters<typeof handleSave>[0] | null>(null);

  const onFormSubmit = (data: Parameters<typeof handleSave>[0]) => {
    if (props.readOnly) return;
    // Show confirmation modal before saving
    setPendingData(data);
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmModal(false);
    if (pendingData) {
      await handleSave(pendingData);
      setPendingData(null);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onFormSubmit)} className="w-full bg-white rounded-lg shadow-sm p-4 overflow-hidden">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <CuppingModeSelector
              mode={mode}
              setMode={setMode}
              readOnly={props.readOnly}
            />
          </div>
          <CuppingMetaFields
            register={register}
            readOnly={props.readOnly}
          />
        </div>
        {!props.readOnly && (
          <div className="mb-6">
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">{t('cuppingProfileForm.lockedTitle')}</p>
                <p className="text-xs text-amber-700 mt-1">
                  {t('cuppingProfileForm.lockedMessage')}
                </p>
              </div>
            </div>
          </div>
        )}
        <CuppingFormSections
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          maxScore={maxScore}
          mode={mode}
          scaScore={scaComputedFinalScore}
          readOnly={props.readOnly}
        />
        <CuppingFormFooter readOnly={props.readOnly} onCancel={props.onCancel} />
      </form>

      {/* Confirmation Modal - Profile cannot be edited after creation */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t('cuppingProfileForm.confirmSaveTitle')}
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <svg className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('cuppingProfileForm.lockedTitle')}</p>
              <p className="text-xs text-amber-700 mt-1">
                {t('cuppingProfileForm.lockedMessage')}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowConfirmModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('auth.common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirmSave}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('purchaseOrder.form.modals.proceed')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
